import React, { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { SkeletonGrid } from '../components/Skeleton';
import { useExhibition } from '../context/ExhibitionContext';
import { useExhibitionsAndPavilions } from '../hooks/useExhibitionsHook';
import { useLanguage, i18n } from '../context/LanguageContext';
import { storageService } from '../utils/storageService';
import { haptics } from '../utils/haptics';
import { apiService } from '../utils/apiService';
import { AuthContext } from '../context/AuthContext';
// Assets
import ekthetsIcon from '../assets/EKTHETES_ICON.png';
import ekthemataIcon from '../assets/EKTHEMATA_ICON.png'
import KeyIcon from '../assets/KEY_ICON.png'
import MapIcon from '../assets/MAP_ICON.png'
import PavIcon from '../assets/PAVILION_ICON.png'
import QrIcon from '../assets/QR_ICON.png'
import UserIcon from '../assets/UTF-8USER_ICON.png'
import InfoIcon from '../assets/INFO_ICON.png'
import EventIcon from '../assets/EVENT_ICON.png'
import TicketIcon from '../assets/TICKET_ICON.png' // Make sure to add this

const ENABLE_LOGIN = false;

function Home() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { selectedExhibition, setSelectedExhibition, exhibitions, setExhibitions } = useExhibition();
  const { exhibitions: fetchedExhibitions, loading, error } = useExhibitionsAndPavilions();
  const [loggedInExhibitions, setLoggedInExhibitions] = useState({});
  const [hasProfileID, setHasProfileID] = useState(false);
  
  const exhibitionsSetRef = useRef(false);
   
  const { 
    isLoggedInToExhibition, 
    logoutFromExhibition, 
    switchExhibition,
    isLoggedInAsVisitor,
    logoutAsVisitor,
    visitorData
  } = useContext(AuthContext);
  
  // Fix the infinite loop by using a ref to track if exhibitions have been set
  useEffect(() => {
    if (fetchedExhibitions && fetchedExhibitions.length > 0 && !exhibitionsSetRef.current) {
      setExhibitions(fetchedExhibitions);
      exhibitionsSetRef.current = true;
    }
  }, [fetchedExhibitions, setExhibitions]);

  // Reset the ref when fetchedExhibitions changes from empty to populated
  useEffect(() => {
    if (!fetchedExhibitions || fetchedExhibitions.length === 0) {
      exhibitionsSetRef.current = false;
    }
  }, [fetchedExhibitions]);

  // Updated ProfileID checking using storage service - Only if login enabled
  useEffect(() => {
    if (!ENABLE_LOGIN) return;
    
    const checkProfileID = () => {
      const isProfileValid = storageService.hasValidProfileID();
      
      if (isProfileValid !== hasProfileID) {
        setHasProfileID(isProfileValid);
      }
    };
    
    checkProfileID();
    window.addEventListener('storage', checkProfileID);
    
    return () => {
      window.removeEventListener('storage', checkProfileID);
    };
  }, [hasProfileID, ENABLE_LOGIN]);
  
  useEffect(() => {
    if (!ENABLE_LOGIN) return;
    
    if (selectedExhibition) {
      const isProfileValid = storageService.hasValidProfileID();
      
      if (isProfileValid !== hasProfileID) {
        setHasProfileID(isProfileValid);
      }
    }
  }, [selectedExhibition, hasProfileID, ENABLE_LOGIN]);
  
  // Updated to use storage service - Only if login enabled
  useEffect(() => {
    if (!ENABLE_LOGIN) return;
    
    const checkUserData = () => {
      try {
        const loggedInStatus = storageService.getAllLoggedInExhibitions();
        setLoggedInExhibitions(loggedInStatus);
      } catch (error) {
        console.error('Error checking user data:', error);
      }
    };
    
    checkUserData();
    
    window.addEventListener('storage', checkUserData);
    return () => window.removeEventListener('storage', checkUserData);
  }, [ENABLE_LOGIN]);
  
  const isLoggedInToCurrentExhibition = () => {
    if (!ENABLE_LOGIN) return false;
    return (selectedExhibition && isLoggedInToExhibition(selectedExhibition.value)) || hasProfileID;
  };
  
  const handleExhibitionSelect = (exhibition) => {
    haptics.select();
    setSelectedExhibition(exhibition);
    
    if (ENABLE_LOGIN && isLoggedInToExhibition(exhibition.value)) {
      switchExhibition(exhibition.value);
    }
  };
  
  const handleLoginClick = () => {
    if (!ENABLE_LOGIN) return;
    
    if (selectedExhibition) {
      setSelectedExhibition(selectedExhibition);
      navigate('/user-type-selection');
    } else {
      navigate('/user-type-selection');
    }
  };
  
  const handleQrScannerClick = () => {
    if (!ENABLE_LOGIN) return;
    navigate('/qr-scanner');
  };
  
  const handleProfileClick = () => {
    if (!ENABLE_LOGIN) return;
    navigate('/visitor-profile');
  };

  // Navigation handlers for tickets and services
  const handleTicketClick = () => {
   // console.log('Navigating to tickets page for exhibition:', selectedExhibition?.label);
    navigate('/tickets');
  };

  const handleSponsorsClick = () => {
   // console.log('Navigating to tickets page for exhibition:', selectedExhibition?.label);
    navigate('/sponsors');
  };

  const handleServicesClick = () => {
   // console.log('Navigating to services page for exhibition:', selectedExhibition?.label);
    navigate('/services');
  };

  //console.log('Current exhibition:', selectedExhibition);
  // Check if current exhibition has tickets
  const hasTickets = selectedExhibition?.HasTicket === 1;
  const hasServices = selectedExhibition?.HasServices === 1;
  const hasSponsors = selectedExhibition?.hasSponsors === 1;
  // Updated logout handling with storage service - Only if login enabled
  const handleLogout = () => {
    if (!ENABLE_LOGIN) return;
    
    if (isLoggedInAsVisitor()) {
      logoutAsVisitor();
    } else if (selectedExhibition) {
      logoutFromExhibition(selectedExhibition.value);
      
      // Use API service method which now uses storage service
      apiService.logoutFromExhibition(selectedExhibition.value);
      
      setLoggedInExhibitions(prev => {
        const updated = { ...prev };
        delete updated[selectedExhibition.value];
        return updated;
      });
    }
    
    // Use storage service
    storageService.removeProfileID();
    setHasProfileID(false);
    
    // Clear all cached data on logout
    apiService.clearUserCache();
  };
  
  const MenuOption = ({ icon, label, onClick, variant }) => {
    const variantClass = variant ? `${variant}-option` : '';
    return (
      <Col xs={6} md={4} lg={3} className="mb-3">
        <Card
          className={`menu-option h-100 ${variantClass}`}
          onClick={() => { haptics.tap(); onClick?.(); }}
        >
          <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center">
            <span className="menu-icon-chip">
              <img src={icon} alt="" className="menu-icon" />
            </span>
            <span className="menu-label">
              {label}
            </span>
          </Card.Body>
        </Card>
      </Col>
    );
  };
  
  const renderExhibitionSelection = () => {
    if (loading) {
      return (
        <Container fluid className="app-container">
          <SkeletonGrid count={6} />
        </Container>
      );
    }
    
    // Use storage service method - Only if login enabled
    const hasProfile = ENABLE_LOGIN ? storageService.hasValidProfileID() : false;
    
    return (
      <Container fluid className="app-container">
        <div className="exhibition-select-intro">
          <span className="exhibition-select-eyebrow">
            {language === 'en' ? 'Choose your exhibition' : 'Επιλέξτε έκθεση'}
          </span>
          <h1 className="exhibition-select-title">
            {language === 'en' ? 'Welcome' : 'Καλώς ήρθατε'}
          </h1>
        </div>

        <Row className="g-3 pb-4">
          {exhibitions && exhibitions.map((exhibition) => (
            <Col key={exhibition.value} xs={12} md={6} lg={4}>
              <Card
                className="exhibition-card h-100"
                onClick={() => handleExhibitionSelect(exhibition)}
              >
                <div className="exhibition-card-image">
                  {exhibition.bannerUrl ? (
                    <img
                      src={exhibition.bannerUrl}
                      alt={exhibition.label}
                      loading="lazy"
                    />
                  ) : (
                    <div className="exhibition-card-fallback">
                      <span>{exhibition.label[0]}</span>
                    </div>
                  )}
                  {/* Floating badges over the image */}
                  <div className="exhibition-card-badges">
                    {ENABLE_LOGIN && (loggedInExhibitions[exhibition.value] || hasProfile) && (
                      <span className="exhibition-card-badge logged-in">
                        {i18n.t('loggedIn') || 'Logged In'}
                      </span>
                    )}
                    {exhibition.HasTicket === 1 && (
                      <span className="exhibition-card-badge tickets">
                        <i className="bi bi-ticket-perforated" />
                        {language === 'en' ? 'Tickets' : 'Εισιτήρια'}
                      </span>
                    )}
                  </div>
                </div>

                <Card.Body className="exhibition-card-body">
                  <div className="exhibition-card-text">
                    <span className="exhibition-card-eyebrow">
                      {language === 'en' ? 'Tap to enter' : 'Πατήστε για είσοδο'}
                    </span>
                    <Card.Title className="exhibition-card-title">{exhibition.label}</Card.Title>
                  </div>
                  <span className="exhibition-card-arrow" aria-hidden="true">
                    <i className="bi bi-arrow-right" />
                  </span>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    );
  };
  
  const renderMainMenu = () => {
    const isExhibitorLoggedIn = ENABLE_LOGIN ? isLoggedInToCurrentExhibition() : false;
    const isVisitorLoggedIn = ENABLE_LOGIN ? isLoggedInAsVisitor() : false;
    const isAnyUserLoggedIn = ENABLE_LOGIN ? (isExhibitorLoggedIn || isVisitorLoggedIn || hasProfileID) : false;
    
    return (
      <Container fluid className="app-container">
        {/* Pure-CSS hero — no image, no cropping, just typography + brand gradient */}
        <div className="exhibition-hero">
          {ENABLE_LOGIN && (isVisitorLoggedIn || isExhibitorLoggedIn || hasProfileID) && (
            <button
              type="button"
              onClick={handleLogout}
              className="exhibition-hero-logout"
            >
              {i18n.t('logout') || 'Logout'}
            </button>
          )}

          <span className="exhibition-hero-eyebrow">
            {i18n.t('welcomeExhibitionText') || 'Welcome to'}
          </span>
          <h1 className="exhibition-hero-title">
            {language === 'en' ? selectedExhibition?.labelEng : selectedExhibition?.label}
          </h1>

          {hasTickets && (
            <div className="exhibition-hero-badges">
              <span className="exhibition-hero-badge ticket">
                <i className="bi bi-ticket-perforated" />
                {language === 'en' ? 'Tickets Available' : 'Διαθέσιμα Εισιτήρια'}
              </span>
            </div>
          )}
        </div>
        
        {/* Menu Grid */}
        <Row>
          <MenuOption
            icon={ekthetsIcon}
            label={i18n.t('companies') || 'Companies'}
            onClick={() => navigate('/exhibitors')}
          />
          <MenuOption
            icon={ekthemataIcon}
            label={i18n.t('exhibits') || 'Exhibits'}
            onClick={() => navigate('/exhibits')}
          />
          <MenuOption
            icon={PavIcon}
            label={i18n.t('pavilions') || 'Pavilions'}
            onClick={() => navigate('/pavilions')}
          />
          <MenuOption
            icon={MapIcon}
            label={i18n.t('mapScreen') || 'Map'}
            onClick={() => navigate('/map')}
          />
          <MenuOption
            icon={InfoIcon}
            label={i18n.t('info') || 'Info'}
            onClick={() => navigate('/info')}
          />
          <MenuOption
            icon={EventIcon}
            label={i18n.t('events') || 'Events'}
            onClick={() => navigate('/events')}
          />
          
          {/* Conditional Ticket Button - Show when HasTicket = 1 */}
          {hasTickets && (
            <MenuOption
              icon={TicketIcon}
              label={i18n.t('ticketButton') || 'Tickets'}
              onClick={handleTicketClick}
              variant="ticket"
            />
          )}

          {hasSponsors && (
            <MenuOption
              icon={EventIcon}
              label='Sponsors'
              onClick={handleSponsorsClick}
              variant="sponsors"
            />
          )}

          {hasServices && (
            <MenuOption
              icon={EventIcon}
              label={i18n.t('servicesButton') || 'Services'}
              onClick={handleServicesClick}
              variant="services"
            />
          )}
          
          {/* Conditional login/profile/QR options - Only show if login enabled */}
          {ENABLE_LOGIN && (
            <>
              {(isVisitorLoggedIn || hasProfileID) ? (
                <MenuOption
                  icon={UserIcon}
                  label={i18n.t('Profil') || 'Profile'}
                  onClick={handleProfileClick}
                />
              ) : isExhibitorLoggedIn ? (
                <MenuOption
                  icon={QrIcon}
                  label={i18n.t('scanQrCode') || 'Scan QR Code'}
                  onClick={handleQrScannerClick}
                />
              ) : (
                <MenuOption
                  icon={KeyIcon}
                  label={i18n.t('login') || "Login"}
                  onClick={handleLoginClick}
                />
              )}
            </>
          )}
        </Row>

      </Container>
    );
  };
  
  return (
    <div className="min-vh-100">
      {!selectedExhibition ? renderExhibitionSelection() : renderMainMenu()}
    </div>
  );
}

export default Home;